import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Button, Typography, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, IconButton, Chip, CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { supplierAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EnhancedPagination from '../../components/common/EnhancedPagination';
import SupplierFormDialog from './SupplierFormDialog';

const Suppliers = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();

    // ── Permissions ───────────────────────────────────────────────────────────
    const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'suppliers' && p.operations.includes('view'));
    const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'suppliers' && p.operations.includes('create'));
    const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'suppliers' && p.operations.includes('update'));
    const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'suppliers' && p.operations.includes('delete'));

    // ── State ─────────────────────────────────────────────────────────────────
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [privilegeError, setPrivilegeError] = useState(false);
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({
        page: 1, limit: 10, total: 0, pages: 1
    });

    // ── Dialog state ──────────────────────────────────────────────────────────
    const [formOpen, setFormOpen] = useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [supplierToDelete, setSupplierToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchSuppliers = useCallback(async () => {
        setLoading(true);
        setPrivilegeError(false);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                sortBy: 'name',
                sortOrder: 'asc',
            };
            if (search.trim()) params.search = search.trim();

            const res = await supplierAPI.getAll(params);
            setSuppliers(res.data?.data || []);
            setPagination(prev => ({
                ...prev,
                total: res.data?.pagination?.total ?? 0,
                pages: res.data?.pagination?.pages ?? 1,
            }));
        } catch (err) {
            if (err.response?.status === 403) {
                setPrivilegeError(true);
            } else {
                toast.error(t('suppliers.fetchError'));
            }
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, search, t]);

    useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
    }, [search]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpenCreate = () => { setSelectedSupplier(null); setFormOpen(true); };
    const handleOpenEdit = (s) => { setSelectedSupplier(s); setFormOpen(true); };
    const handleFormClose = () => { setFormOpen(false); setSelectedSupplier(null); };
    const handleFormSuccess = () => { handleFormClose(); fetchSuppliers(); };

    const handleDeleteClick = (s) => { setSupplierToDelete(s); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!supplierToDelete) return;
        setDeleteLoading(true);
        try {
            await supplierAPI.delete(supplierToDelete._id);
            toast.success(t('suppliers.deleteSuccess'));
            setDeleteDialogOpen(false);
            setSupplierToDelete(null);
            fetchSuppliers();
        } catch (err) {
            toast.error(err.response?.data?.message || t('suppliers.deleteError'));
        } finally {
            setDeleteLoading(false);
        }
    };

    // ── Privilege gate ────────────────────────────────────────────────────────
    if (!canView || privilegeError) {
        return (
            <NoPrivilege
                message={t('suppliers.noPrivilegeError', 'You do not have permission to view suppliers.')}
            />
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>

            {/* ── Header ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    {t('suppliers.title')}
                </Typography>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        {t('suppliers.addButton')}
                    </Button>
                )}
            </Box>

            {/* ── Search ── */}
            <Box mb={2}>
                <TextField
                    size="small"
                    placeholder={t('suppliers.searchPlaceholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ width: 320 }}
                />
            </Box>

            {/* ── Table ── */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>{t('suppliers.name')}</strong></TableCell>
                            <TableCell><strong>{t('suppliers.phone')}</strong></TableCell>
                            <TableCell><strong>{t('suppliers.email')}</strong></TableCell>
                            <TableCell><strong>{t('suppliers.address')}</strong></TableCell>
                            <TableCell><strong>{t('common.status')}</strong></TableCell>
                            <TableCell align="center"><strong>{t('common.actions')}</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : suppliers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        {t('suppliers.noItemsFound')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : suppliers.map(supplier => (
                            <TableRow key={supplier._id} hover>
                                <TableCell>
                                    <Typography fontWeight={500}>{supplier.name}</Typography>
                                </TableCell>
                                <TableCell>{supplier.phone || '—'}</TableCell>
                                <TableCell>{supplier.email || '—'}</TableCell>
                                <TableCell>
                                    <Typography
                                        color="text.secondary"
                                        sx={{
                                            maxWidth: 200,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {supplier.address || '—'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={supplier.isActive ? t('common.active') : t('common.inactive')}
                                        color={supplier.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    {canEdit && (
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => handleOpenEdit(supplier)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {canDelete && (
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeleteClick(supplier)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* ── Pagination ── */}
            {!loading && suppliers.length > 0 && (
                <Box mt={2}>
                    <EnhancedPagination
                        pagination={pagination}
                        onPageChange={p => setPagination(prev => ({ ...prev, page: p }))}
                        onLimitChange={l => setPagination(prev => ({ ...prev, limit: l, page: 1 }))}
                    />
                </Box>
            )}

            {/* ── Dialogs ── */}
            {canAdd && (
                <SupplierFormDialog
                    open={formOpen}
                    onClose={handleFormClose}
                    onSuccess={handleFormSuccess}
                    supplier={selectedSupplier}
                />
            )}

            {canDelete && (
                <ConfirmDialog
                    open={deleteDialogOpen}
                    title={t('suppliers.deleteConfirmTitle')}
                    message={t('suppliers.deleteConfirmMessage')}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => { setDeleteDialogOpen(false); setSupplierToDelete(null); }}
                    loading={deleteLoading}
                />
            )}

        </Box>
    );
};

export default Suppliers;